import { useCallback, useEffect } from 'react';
import { publish } from '../../../publisher';
import { isWindowExists } from '../../../utils';

/**
 * Custom hook for managing swatch state and interactions
 * @param {Object} params - Hook parameters
 * @param {Object} params.state - Current reducer state
 * @param {Function} params.dispatch - Reducer dispatch function
 * @param {Object} params.data - Product data from useDataModel
 * @param {string} params.parentId - Parent product ID
 * @param {Function} params.onChange - onChange callback
 * @param {boolean} params.swatchNavigatestoPip - Whether swatch should navigate to PIP
 * @param {Function} params.onHover - Hover callback function
 * @param {string} params.itemId - Current item ID
 * @returns {Object} Event handlers for swatch interactions
 */
export const useSwatchState = ({
  state,
  dispatch,
  data,
  parentId,
  onChange,
  swatchNavigatestoPip,
  onHover,
  itemId
}) => {
  // Initialize swatches from product data
  useEffect(() => {
    if (!data?.product?.info) return;

    const { swatches = [], totalNumberOfOptions = 0 } = data.product.info;

    if (swatches?.length && !state.shouldSkip) {
      const firstSwatchLabel = swatches[0]?.label || null;
      dispatch({
        type: 'INIT_SWATCHES',
        payload: {
          swatches,
          totalNumberOfOptions,
          parentId,
          firstSwatchLabel
        }
      });
    } else if (totalNumberOfOptions > 0) {
      dispatch({
        type: 'UPDATE_TOTAL_OPTIONS',
        payload: { totalNumberOfOptions }
      });
    }
  }, [data?.product?.info, state.shouldSkip, parentId, dispatch]);

  // Handle swatch click events and publish
  useEffect(() => {
    if (state?.isSwatchClicked) {
      publish('click-swatch', {});

      const selectedItem = state.swatches.find((swatch) => swatch.isSelected);
      const selectedItemId = selectedItem?.itemId || '';

      onChange({ itemId: selectedItemId });

      dispatch({ type: 'RESET_CLICK_STATE' });
    }
  }, [state.isSwatchClicked, state.swatches, onChange, dispatch]);

  const updateSwatchSelection = useCallback((swatches, selectedId) => {
    return swatches.map((swatch) => ({
      ...swatch,
      isSelected: swatch?.itemId === selectedId
    }));
  }, []);

  // Event handlers
  const handleAttributeValueClick = useCallback((clickedItemId) => {
    const newSwatches = updateSwatchSelection(state.swatches, clickedItemId);
    const selectedItem = newSwatches.find((swatch) => swatch.isSelected);

    if (swatchNavigatestoPip && selectedItem?.url) {
      if (isWindowExists() && window.location && typeof window.location.assign === 'function') {
        window.location.assign(selectedItem.url);
      }
    } else {
      dispatch({
        type: 'SWATCH_CLICKED',
        payload: {
          swatches: newSwatches,
          selectedItemLabel: selectedItem?.label || ''
        }
      });
    }
  }, [state.swatches, updateSwatchSelection, swatchNavigatestoPip, dispatch]);

  const handleAttributeValueHover = useCallback((hoveredItemId) => {
    const hoveredSwatch = state.swatches.find((swatch) => swatch.itemId === hoveredItemId);
    const hoverPayload = {
      itemId: hoveredItemId,
      isHovered: true,
      selectedItemLabel: hoveredSwatch?.label || ''
    };

    onHover(hoverPayload);
    dispatch({
      type: 'UPDATE_HOVER',
      payload: { ...hoverPayload }
    });
  }, [state.swatches, onHover, dispatch]);

  const handleResetHover = useCallback(() => {
    const selectedSwatch = state.swatches.find((swatch) => swatch.isSelected);
    const hoverPayload = {
      itemId: selectedSwatch?.itemId || itemId,
      isHovered: false,
      selectedItemLabel: selectedSwatch?.label || ''
    };

    onHover(hoverPayload);
    dispatch({
      type: 'UPDATE_HOVER',
      payload: { ...hoverPayload }
    });
  }, [state.swatches, onHover, itemId, dispatch]);

  return {
    handleAttributeValueClick,
    handleAttributeValueHover,
    handleResetHover
  };
};
